// AutoCompleteContaBancariaSimples.tsx
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../../shared/hooks';
import { ContaBancariaService } from '../services/api/cadastros/ContaBancaria';

type TAutoCompleteOption = {
  idconta: string;
  label: string;
}

interface IAutoCompleteContaBancariaSimpleProps {
  isExternalLoading?: boolean;
  label: string;
  value?: string;
  onChange?: (idconta: string, label?: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  // Filtra pelo tipo da conta financeira (ex.: 'banco') — ver TTipoContaFinanceira.
  tipo?: string;
  // Ref pro input nativo — usado pra navegação por teclado externa (ex.:
  // foco automático vindo do SeletorFormasPagamento).
  inputRef?: React.Ref<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  // Ocupa a largura total do container — sem isso o Autocomplete encolhe
  // pro tamanho do conteúdo e corta o nome do banco/titular selecionado.
  fullWidth?: boolean;
}

export const AutoCompleteContaBancariaSimple: React.FC<IAutoCompleteContaBancariaSimpleProps> = ({
  isExternalLoading = false,
  label,
  value,
  onChange,
  error = false,
  helperText = '',
  disabled = false,
  tipo,
  inputRef,
  onKeyDown,
  fullWidth = false,
}) => {
  const { debounce } = useDebounce();
  const [selectedId, setSelectedId] = useState<string | undefined>(value);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  // Sincronizar com o valor externo
  useEffect(() => {
    setSelectedId(value);
  }, [value]);

  // Buscar opções
  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ContaBancariaService.getAllList(busca, selectedId?.toString(), tipo)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setOpcoes([]);
          } else {
            setOpcoes(result.data.map(conta => ({ idconta: conta.idconta, label: `${conta.numeroconta} - ${conta.titular}` })));
          }
        });
    });
  }, [busca, debounce, tipo]); // Adicione debounce às dependências

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;
    const selectedOption = opcoes.find(opcao => opcao.idconta === selectedId);
    return selectedOption || null;
  }, [selectedId, opcoes]);

  // Tipo correto para o evento do Autocomplete do MUI
  const handleChange = (_event: React.SyntheticEvent, newValue: TAutoCompleteOption | null) => {
    const newId = newValue?.idconta || '';
    setSelectedId(newId);
    onChange?.(newId, newValue?.label);
    setBusca('');
  };

  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'
      disablePortal
      fullWidth={fullWidth}
      // Abre a listagem já ao focar (não só ao clicar/digitar) — precisa
      // disso pro fluxo de teclado do SeletorFormasPagamento (Enter no
      // Valor pula pra cá com foco programático) já cair com as opções à
      // vista, prontas pra navegar com seta pra baixo sem digitar nada.
      openOnFocus
      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading || disabled}
      value={autoCompleteSelectedOption}
      onInputChange={(_event: React.SyntheticEvent, newValue: string) => setBusca(newValue)}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          size='small'
          label={label}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};